import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import { ThemeContext } from '../contexts/ThemeContext';
import { authFetch } from '../services/authFetch';
import { requestService, studentStatsService } from '../services/api';

const ChatbotButton = styled.button`
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 2000;
  background: var(--primary, #3354a5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  box-shadow: 0 4px 16px rgba(51, 84, 165, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  font-size: 2rem;
  padding: 0;
  overflow: hidden;
  &:hover {
    background: #2a4580;
    box-shadow: 0 8px 24px rgba(51, 84, 165, 0.25);
    transform: translateY(-2px) scale(1.05);
  }
  @media (max-width: 576px) {
    width: 48px;
    height: 48px;
    bottom: 16px;
    right: 16px;
    font-size: 1.5rem;
  }
`;

const WhatsAppButton = styled.button`
  position: fixed;
  bottom: 112px;
  right: 32px;
  z-index: 2000;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  font-size: 2rem;
  padding: 0;
  overflow: hidden;
  &:hover {
    background: #20B954;
    box-shadow: 0 8px 24px rgba(37, 211, 102, 0.25);
    transform: translateY(-2px) scale(1.05);
  }
  @media (max-width: 576px) {
    width: 48px;
    height: 48px;
    bottom: 80px;
    right: 16px;
    font-size: 1.5rem;
  }
`;

const WhatsAppLabel = styled.div`
  position: fixed;
  bottom: 120px;
  right: 108px;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translateX(10px);
  transition: opacity 0.2s, transform 0.2s;
  ${WhatsAppButton}:hover + & {
    opacity: 1;
    transform: translateX(0);
  }
  @media (max-width: 576px) {
    bottom: 88px;
    right: 72px;
    font-size: 0.75rem;
    padding: 0.375rem 0.5rem;
  }
`;

const ChatIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const WhatsAppIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block' }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z"/>
  </svg>
);

// Add this above the ProfilePage component
const ChatbotModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ChatbotModalContent = styled.div`
  background: var(--background, #fff);
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  width: 420px;
  max-width: 95vw;
  height: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  @media (max-width: 576px) {
    width: 98vw;
    height: 90vh;
    border-radius: 10px;
  }
`;
const ChatbotModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.5rem 1.25rem;
  background: var(--primary, #3354a5);
  color: #fff;
`;
const ChatbotModalTitle = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
`;
const ChatbotModalClose = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0 0.5rem;
  line-height: 1;
`;
const ChatbotIframe = styled.iframe`
  flex: 1;
  border: none;
  width: 100%;
  min-height: 0;
  background: #f8f8f8;
`;

const ProfilePage = ({ profileMode=false }) => {
  // Get user data from API
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  
  // Statistics data states
  const [statsData, setStatsData] = useState({
    average_points: 0,
    total_jam_kersos: 0,
    total_jam_lembur: 0,
    total_poin_apresiasi: 0,
    ketidakhadiran: {
      sakit: 0,
      ijin: 0,
      alpha: 0
    },
    surat_peringatan: {
      latest_type: null,
      count: 0
    },
    total_poin_sikap: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Detail data states
  const [kersosDetails, setKersosDetails] = useState([]);
  const [lemburDetails, setLemburDetails] = useState([]);
  const [apresiasiDetails, setApresiasiDetails] = useState([]);
  const [ketidakhadiranDetails, setKetidakhadiranDetails] = useState([]);
  const [spDetails, setSpDetails] = useState([]);
  const [poinSikapDetails, setPoinSikapDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [kompenStats, setKompenStats] = useState({
    sisa_kompen_murni: 0,
    sisa_kompen_alat: 0,
    total_redeemed: 0
  });

  // Add state for modal
  const [showChatbot, setShowChatbot] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        const response = await authFetch(`${baseUrl}api/student/me`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Gagal memuat data profil.');
        const data = await response.json();
        setUser(data);
      } catch (e) {
        setUser({});
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // Fetch recent requests from individual endpoints
  useEffect(() => {
    const fetchRecentRequests = async () => {
      setRequestsLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        
        // Fetch from all individual endpoints
        const endpoints = [
          { url: `${baseUrl}api/interuption_permits`, type: 'ijin_interupsi' },
          { url: `${baseUrl}api/absent_permits`, type: 'ijin_tidakmasuk' },
          { url: `${baseUrl}api/earlyleave_permits`, type: 'ijin_pulangawal' },
          { url: `${baseUrl}api/kersos_reports`, type: 'laporan_kersos' },
          { url: `${baseUrl}api/overtime_reports`, type: 'laporan_lembur' },
          { url: `${baseUrl}api/psm_reports`, type: 'laporan_psm' }
        ];

        const fetchPromises = endpoints.map(async (endpoint) => {
          try {
            const response = await authFetch(endpoint.url, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
            });
            
            if (response.ok) {
              const data = await response.json();
              
              // Extract the array from different possible response formats
              let items = [];
              if (data.data && Array.isArray(data.data)) {
                items = data.data;
              } else if (Array.isArray(data)) {
                items = data;
              } else if (data.items && Array.isArray(data.items)) {
                items = data.items;
              }
              
              // Add type information to each item
              return items.map(item => ({
                ...item,
                type: endpoint.type,
                source_endpoint: endpoint.url
              }));
            } else {
              console.warn(`Failed to fetch from ${endpoint.url}: ${response.status}`);
              return [];
            }
          } catch (error) {
            console.error(`Error fetching from ${endpoint.url}:`, error);
            return [];
          }
        });

        // Wait for all requests to complete
        const results = await Promise.all(fetchPromises);
        
        // Combine all results
        const allRequests = results.flat();
        
        // Sort by date (newest first) and take the latest 6
        const sortedRequests = allRequests
          .filter(item => item && item.id) // Filter out invalid items
          .sort((a, b) => {
            const dateA = new Date(a.created_at || a.tanggal || a.updated_at || 0);
            const dateB = new Date(b.created_at || b.tanggal || b.updated_at || 0);
            return dateB - dateA; // Newest first
          })
          .slice(0, 6);
        
        const mappedRequests = mapRequestsToActivities(sortedRequests);
        setRecentRequests(mappedRequests);
        
      } catch (error) {
        console.error('Failed to fetch recent requests:', error);
        setRecentRequests([]);
      } finally {
        setRequestsLoading(false);
      }
    };

    fetchRecentRequests();
  }, []);

  // Fetch student statistics data
  useEffect(() => {
    const fetchStudentStats = async () => {
      setStatsLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        
        // Fetch all statistics data in parallel
        const [
          kersosResponse,
          lemburResponse,
          apresiasiResponse,
          ketidakhadiranResponse,
          spResponse,
          poinSikapResponse,
          poinSikapDetailsResponse
        ] = await Promise.all([
          authFetch(`${baseUrl}api/student/jam-kersos`),
          authFetch(`${baseUrl}api/student/jam-lembur`), 
          authFetch(`${baseUrl}api/student/poin-apresiasi`),
          authFetch(`${baseUrl}api/student/ketidakhadiran`),
          authFetch(`${baseUrl}api/student/surat-peringatan`),
          authFetch(`${baseUrl}api/student/poin-sikap`),
          authFetch(`${baseUrl}api/student/poin-sikap-details`)
        ]);

        // Process kersos data
        if (kersosResponse.ok) {
          const kersosData = await kersosResponse.json();
          const kersosRecords = kersosData.data || [];
          const totalKersos = kersosRecords.reduce((sum, record) => {
            const hour = parseFloat(record.hour) || 0;
            return sum + hour;
          }, 0);
          setKersosDetails(kersosRecords.map(record => ({
            tanggal: record.date || record.created_at,
            pelanggaran: record.pelanggaran,
            description: record.description,
            jam: record.hour > 0 ? `+${record.hour}` : record.hour.toString()
          })));
          setStatsData(prev => ({ ...prev, total_jam_kersos: totalKersos }));
        }

        // Process lembur data
        if (lemburResponse.ok) {
          const lemburData = await lemburResponse.json();
          const lemburRecords = lemburData.data || [];
          const totalLembur = lemburRecords.reduce((sum, record) => {
            const hour = parseFloat(record.hour) || 0;
            return sum + hour;
          }, 0);
          setLemburDetails(lemburRecords.map(record => ({ 
            jenis: record.type || 'Lembur',
            tanggal: record.date || record.created_at,
            keterangan: record.description,
            kategori: record.pelanggaran?.description || 'N/A',
            jam: record.hour > 0 ? `+${parseFloat(record.hour).toFixed(2).replace(/\.?0+$/, '')}` : parseFloat(record.hour).toFixed(2).replace(/\.?0+$/, '')
          })));
          setStatsData(prev => ({ ...prev, total_jam_lembur: totalLembur }));
          
          // Set kompen stats if available
          if (lemburData.kompen_stats) {
            setKompenStats(lemburData.kompen_stats);
          }
        }

        // Process apresiasi data
        if (apresiasiResponse.ok) {
          const apresiasiData = await apresiasiResponse.json();
          const apresiasiRecords = apresiasiData.data || [];
          const totalApresiasi = apresiasiRecords.reduce((sum, record) => {
            const point = parseFloat(record.point) || 0;
            return sum + point;
          }, 0);
          setApresiasiDetails(apresiasiRecords.map(record => ({
            tanggal: record.date || record.created_at,
            jenis: record.description,
            poin: record.point > 0 ? `+${record.point}` : record.point.toString()
          })));
          setStatsData(prev => ({ ...prev, total_poin_apresiasi: totalApresiasi }));
        }

        // Process ketidakhadiran data
        if (ketidakhadiranResponse.ok) {
          const ketidakhadiranData = await ketidakhadiranResponse.json();
          const ketidakhadiranRecords = ketidakhadiranData.data || [];
          const ketidakhadiranStats = ketidakhadiranRecords.reduce((acc, record) => {
            const type = record.type?.toLowerCase();
            if (type === 'sakit') acc.sakit += record.days || 1;
            else if (type === 'ijin') acc.ijin += record.days || 1;
            else if (type === 'alfa' || type === 'alpha') acc.alpha += record.days || 1;
            return acc;
          }, { sakit: 0, ijin: 0, alpha: 0 });
          
          // Fetch class type information for each ketidakhadiran record
          // Since the individual record endpoints don't exist, we'll try to get more accurate data
          // by cross-referencing with any existing permit data
          const ketidakhadiranDetailsWithKelas = await Promise.all(
            ketidakhadiranRecords.map(async (record) => {
              let kelas = 'Teori'; // Default value
              
              // Try to get more accurate class type information
              try {
                if (record.ijin_tidakmasuk_id) {
                  // Try to fetch from the student absent_permits endpoint to get activity info
                  // First, try to get the data from the student absent_permits list
                  const absentPermitsResponse = await authFetch(`${baseUrl}api/absent_permits`);
                  if (absentPermitsResponse.ok) {
                    const absentPermitsData = await absentPermitsResponse.json();
                    const permits = absentPermitsData.data || absentPermitsData || [];
                    const matchingPermit = permits.find(permit => permit.id === record.ijin_tidakmasuk_id);
                    
                    if (matchingPermit && matchingPermit.activity) {
                      kelas = matchingPermit.activity;
                    } else {
                      console.log(`No matching permit found for ID ${record.ijin_tidakmasuk_id}`);
                    }
                  } else {
                    console.log(`Failed to fetch absent_permits: ${absentPermitsResponse.status}`);
                  }
                } else if (record.laporan_kts_id) {
                  // Try to fetch from the student kts_reports endpoint to get class_type info
                  // First, try to get the data from the student kts_reports list
                  const ktsReportsResponse = await authFetch(`${baseUrl}api/kts_reports`);
                  if (ktsReportsResponse.ok) {
                    const ktsReportsData = await ktsReportsResponse.json();
                    const reports = ktsReportsData.data || ktsReportsData || [];
                    const matchingReport = reports.find(report => report.id === record.laporan_kts_id);
                    
                    if (matchingReport && matchingReport.class_type) {
                      kelas = matchingReport.class_type;
                    } else {
                      console.log(`No matching report found for ID ${record.laporan_kts_id}`);
                    }
                  } else {
                    console.log(`Failed to fetch kts_reports: ${ktsReportsResponse.status}`);
                  }
                } else {
                  // Direct ketidakhadiran records (not linked to permits)
                  kelas = 'Teori';
                }
              } catch (error) {
                console.error(`Error fetching class type for record ${record.id}:`, error);
                kelas = 'Teori'; // Fallback to default
              }
              
              return {
                laporan_kts_id: record.laporan_kts_id,
                ijin_tidakmasuk_id: record.ijin_tidakmasuk_id,
                tanggal: record.created_at,
                jenis: record.type,
                kelas: kelas,
                hari: record.days || 1,
                kategori: record.student_description ?? record.studentDescription ?? record.type,
                keterangan: record.laporan_description ?? record.laporanDescription ?? record.student_description ?? record.studentDescription
              };
            })
          );
          
          setKetidakhadiranDetails(ketidakhadiranDetailsWithKelas);
          setStatsData(prev => ({ ...prev, ketidakhadiran: ketidakhadiranStats }));
        }

        // Process surat peringatan data
        if (spResponse.ok) {
          const spData = await spResponse.json();
          const spRecords = spData.data || [];
          // Filter only approved SPs with status 'terima'
          const approvedSP = spRecords.filter(sp => sp.status === 'terima');
          // Sort by created_at to get the actual latest SP
          const sortedSP = approvedSP.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const latestSP = sortedSP.length > 0 ? sortedSP[0] : null;
          
          setSpDetails(approvedSP.map(record => ({
            tanggal: record.created_at || record.decision_date,
            jenis: record.type,
            diterbitkan: record.decision_by?.name || record.issued_by || 'Admin'
          })));
          setStatsData(prev => ({ 
            ...prev, 
            surat_peringatan: {
              latest_type: latestSP?.type || null,
              count: approvedSP.length
            }
          }));
        }

        // Process poin sikap data
        if (poinSikapResponse.ok) {
            const poinSikapData = await poinSikapResponse.json();
            if (poinSikapData.success) {
                setStatsData(prev => ({ ...prev, total_poin_sikap: poinSikapData.data.total_poin_sikap }));
            }
        }

        // Process poin sikap details data
        if (poinSikapDetailsResponse.ok) {
          const poinSikapDetailsData = await poinSikapDetailsResponse.json();
          if (poinSikapDetailsData.success) {
            setPoinSikapDetails(poinSikapDetailsData.data || []);
          }
        }

        // Get average points from user data
        if (user.average_points !== undefined) {
          setStatsData(prev => ({ ...prev, average_points: user.average_points || 0 }));
        }

      } catch (error) {
        console.error('Failed to fetch student statistics:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    if (user.id) {
      fetchStudentStats();
    }
  }, [user.id, user.average_points]);

  // Helper function to map API response to activity format
  const mapRequestsToActivities = (requests) => { 
    if (!Array.isArray(requests)) {
      console.warn('Requests is not an array:', requests);
      return [];
    }

    return requests.map(request => {  
      const type = request.type || request.table_name || request.form_type;
      // Get the actual status from the database (request_status field)
      const actualStatus = request.request_status || request.status || 'pending';
      let formType, title, mappedData;

      // Determine form type and title based on request type, and map data appropriately
      switch (type) {
        case 'ijin_interupsi':
          formType = 'interupsi';
          title = `Permohonan Interupsi ${getStatusText(actualStatus)}`;
          // Map data structure for InterupsiForm
          mappedData = {
            classType: request.current_activity || 'Teori',
            departureTime: request.leave_hour || request.departure_time || '',
            returnTime: request.return_hour || request.return_time || '',
            purpose: request.purpose || '',
            destination: request.destination || '',
            responsible: request.pic || request.responsible || '',
            relationship: request.relationship || '',
            phoneNumber: request.pic_contact_number || request.phone_number || '',
            ...request // keep all original fields for fallback
          };
          break;
        case 'ijin_pulangawal':
          formType = 'pulang-awal';
          title = `Permohonan Pulang Awal ${getStatusText(actualStatus)}`;
          // LeaveForm uses the same field names as API response
          mappedData = {
            earlyleave_time: request.earlyleave_time || '',
            purpose: request.purpose || '',
            destination: request.destination || '',
            pic: request.pic || '',
            relationship: request.relationship || '',
            pic_contact_number: request.pic_contact_number || '',
            ...request // keep all original fields for fallback
          };
          break;
        case 'ijin_tidakmasuk':
          formType = 'tidak-masuk';
          title = `Permohonan Tidak Masuk ${getStatusText(actualStatus)}`;
          // SicknessForm uses the same field names as API response
          mappedData = {
            from_date: request.from_date || '',
            to_date: request.to_date || '',
            purpose: request.purpose || '',
            destination: request.destination || '',
            pic: request.pic || '',
            relationship: request.relationship || '',
            pic_contact_number: request.pic_contact_number || '',
            images: request.images || [],
            ...request // keep all original fields for fallback
          };
          break;
        case 'laporan_kersos':
          formType = 'kersos';
          title = `Laporan Kersos ${getStatusText(actualStatus)}`;
          // Map data structure for KersosForm
          mappedData = {
            activity_date: request.activity_date || request.kersos_date || '',
            start_time: request.start_time || '',
            finish_time: request.finish_time || request.end_time || '',
            violation: request.violation || '',
            reflection: request.reflection || '',
            ...request // keep all original fields for fallback
          };
          break;
        case 'laporan_lembur':
          formType = 'kompensasi';
          title = `Laporan Lembur ${getStatusText(actualStatus)}`;
          // Map data structure for KompensasiForm
          mappedData = {
            overtime_date: request.activity_date || request.overtime_date || '',
            start_time: request.start_time || '',
            finish_time: request.finish_time || request.end_time || '',
            purpose: request.purpose || request.violation || '',
            type_or_work: request.type_or_work || request.activity_description || '',
            reflection: request.reflection || '',
            teacher_name: request.teacher_name || request.supervisor || '',
            images: request.images || [],
            ...request // keep all original fields for fallback
          };
          break;
        case 'laporan_psm':
          formType = 'psm';
          title = `Laporan PSM ${getStatusText(actualStatus)}`;
          // Map data structure for PSMForm
          mappedData = {
            psm_date: request.psm_date || request.activity_date || '',
            start_time: request.start_time || '',
            finish_time: request.finish_time || request.end_time || '',
            apresiasis_id: request.apresiasis_id || '',
            description: request.description || request.activity_description || '',
            reflection: request.reflection || '',
            pic: request.pic || request.supervisor || '',
            pic_contact_number: request.pic_contact_number || request.phone_number || '',
            images: request.images || [],
            ...request // keep all original fields for fallback
          };
          break;
        default:
          formType = type || 'unknown';
          title = `Request ${getStatusText(actualStatus)}`;
          mappedData = request;
      }

      const mappedRequest = {
        id: request.id,
        date: request.created_at || request.tanggal || request.updated_at || new Date().toISOString(),
        title: title,
        type: type && type.startsWith('ijin_') ? 'surat-ijin' : 'laporan',
        formType: formType,
        status: actualStatus,
        statusText: getStatusText(actualStatus),
        data: mappedData
      };
      
      return mappedRequest;
    });
  };

  // Helper function to convert status to display text
  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
      case 'disetujui':
        return 'Disetujui';
      case 'rejected':
      case 'ditolak':
        return 'Ditolak';
      case 'pending':
      case 'diproses':
        return 'Diproses';
      case 'menunggu':
        return 'Menunggu';
      default:
        return 'Diajukan';
    }
  };
  const [showKersosDetail, setShowKersosDetail] = useState(false);
  const [showLemburDetail, setShowLemburDetail] = useState(false);
  const [showApresiasiDetail, setShowApresiasiDetail] = useState(false);
  const [showKetidakhadiranDetail, setShowKetidakhadiranDetail] = useState(false);
  const [showSPDetail, setShowSPDetail] = useState(false);

  const handleShowSPDetail = () => {
    setShowSPDetail(true);
  };

  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);

  // Fetch announcements from API
  useEffect(() => {
    const fetchAnnouncements = async () => {
      setAnnouncementsLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        const response = await authFetch(`${baseUrl}api/student/pengumuman`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Gagal memuat pengumuman.');
        const result = await response.json();
        
        // Map API data to match the expected format
        const mappedAnnouncements = result.data.map(announcement => ({
          id: announcement.id,
          title: announcement.title,
          message: announcement.description,
          date: announcement.date,
          type: "info" // Default type since API doesn't have type field
        }));
        
        setAnnouncements(mappedAnnouncements);
      } catch (e) {
        console.error('Failed to fetch announcements:', e);
        setAnnouncements([]);
      } finally {
        setAnnouncementsLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);
  
  // Handle view activity - redirect to appropriate form with data
  const handleViewActivity = (activity) => {
    if (activity.type === 'surat-ijin') {
      // For permission letters (surat-ijin)
      navigate('/surat-ijin', { 
        state: { 
          viewingHistoricalRequest: {
            id: activity.id,
            type: activity.title,
            date: activity.date,
            status: activity.status,
            statusText: activity.statusText,
            formType: activity.formType,
            data: activity.data
          } 
        } 
      });
    } else if (activity.type === 'laporan') {
      // For reports (laporan)
      navigate('/laporan', { 
        state: { 
          viewingHistoricalRequest: {
            id: activity.id,
            type: activity.title,
            date: activity.date,
            status: activity.status,
            statusText: activity.statusText,
            formType: activity.formType,
            data: activity.data
          } 
        } 
      });
    }
  };
  
  // Student data with dynamic activities
  // Debug logging

  const studentData = {
    name: user.name,
    class: user.class,
    nis: user.nis,
    wali_kelas_name: user.wali_kelas_name,
    absenceNumber: user.absenceNumber,
    handphone: user.handphone,
    homeAddress: user.address,
    homeDesc: user.addressDesc,
    fatherName: user.fatherName,
    motherName: user.motherName,
    maidenName: user.maidenName,
    emergencyContactName: user.emergencyContactName,
    emergencyContactPhone: user.emergencyContactPhone,
    emergencyContactRelationship: user.emergencyContactRelationship,
    avatarUrl: user.avatarUrl
      ? (user.avatarUrl.startsWith('http')
          ? user.avatarUrl
          : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${user.avatarUrl.startsWith('/') ? user.avatarUrl : user.avatarUrl}`)
      : '/images/user.png',
    stats: [
      { 
        label: 'Rata-rata Nilai', 
        value: statsLoading ? '-' : statsData.average_points.toString(), 
        icon: '😇', 
        bgColor: '#e6f8f5' 
      },
      { 
        label: 'Jam Kersos', 
        value: statsLoading ? '-' : (statsData.total_jam_kersos > 0 ? '0' : parseFloat(statsData.total_jam_kersos).toFixed(2)), 
        icon: '🤝', 
        bgColor: '#e8f1fd' 
      },
      { 
        label: 'Jam Lembur', 
        value: statsLoading ? '-' : `${statsData.total_jam_lembur > 0 ? '+' : ''}${parseFloat(statsData.total_jam_lembur).toFixed(2).replace(/\.?0+$/, '')}`, 
        icon: '⏰', 
        bgColor: '#fff3e0' 
      },
      { 
        label: 'Poin Apresiasi', 
        value: statsLoading ? '-' : statsData.total_poin_apresiasi.toString(), 
        icon: '🌟', 
        bgColor: '#ffecf2' 
      },
      { 
        label: 'Ketidakhadiran', 
        value: statsLoading ? '-' : `Sakit: ${statsData.ketidakhadiran.sakit} Hari\nIjin: ${statsData.ketidakhadiran.ijin} Hari\nAlpha: ${statsData.ketidakhadiran.alpha} Hari`, 
        icon: '📋', 
        bgColor: '#f0e8fd' 
      },
      { 
        label: 'Poin Sikap & SP', 
        value: statsLoading 
          ? '-' 
          : `Poin Sikap: ${((parseFloat(statsData.total_poin_sikap) || 0) + 100).toFixed(2).replace(/\.?0+$/, '')}\n\nSP: ${statsData.surat_peringatan.latest_type || '-'}`,
        icon: '⚠️', 
        bgColor: '#ffe6e6' 
      }
    ],
    activities: recentRequests // Use dynamic data instead of hardcoded activities
  };

  // Render based on mode (profile or home)
  if (profileMode) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>Data Siswa</h1>
        </PageHeader>
        
        <ProfileContent>
          <MainSection>
            <ProfileCard>
              <ProfileHeader>
                <AvatarContainer>
                  <Avatar 
                    src={studentData.avatarUrl} 
                    alt={studentData.name}
                    onError={(e) => {
                      e.target.src = '/images/user.png';
                    }}
                  />
                </AvatarContainer>
                <ProfileInfo>
                  <h2>{studentData.name}</h2>
                  <InfoRow>
                    <InfoItem>Kelas: {studentData.class}</InfoItem>
                    <InfoDivider>|</InfoDivider>
                    <InfoItem>NIS: {studentData.nis}</InfoItem>
                    {studentData.wali_kelas_name && (
                      <>
                        <InfoDivider>|</InfoDivider>
                        <InfoItem>Wali Kelas: {studentData.wali_kelas_name}</InfoItem>
                      </>
                    )}
                  </InfoRow>
                </ProfileInfo>
              </ProfileHeader>
              
              <DetailSection>
                <DetailContainer>
                  <DetailGroup theme={darkMode ? 'dark' : 'light'}>
                    <DetailHeader>Informasi Pribadi</DetailHeader>
                    <DetailRow>
                      <DetailLabel>Nama Lengkap</DetailLabel>
                      <DetailValue>{studentData.name}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Kelas</DetailLabel>
                      <DetailValue>{studentData.class}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>No Urut</DetailLabel>
                      <DetailValue>{studentData.absenceNumber}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Alamat Domisili</DetailLabel>
                      <DetailValueLong>{studentData.homeAddress}</DetailValueLong>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Keterangan Domisili</DetailLabel>
                      <DetailValue>{studentData.homeDesc}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Nomor HP</DetailLabel>
                      <DetailValue>{studentData.handphone}</DetailValue>
                    </DetailRow>
                  </DetailGroup>
                  
                  <DetailGroup theme={darkMode ? 'dark' : 'light'}>
                    {/* <DetailHeader>Informasi Keluarga</DetailHeader> */}
                    {/* <DetailRow>
                      <DetailLabel>Nama Ayah</DetailLabel>
                      <DetailValue>{studentData.fatherName ? studentData.fatherName : '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Nama Ibu</DetailLabel>
                      <DetailValue>{studentData.motherName ? studentData.motherName : '-'}</DetailValue>
                    </DetailRow> */}
                    {/* <DetailRow>
                      <DetailLabel>Nama Wali</DetailLabel>
                      <DetailValue>{studentData.maidenName ? studentData.maidenName : '-'}</DetailValue>
                    </DetailRow> */}
                    <DetailHeader style={{marginTop: '1.5rem'}}>Kontak Darurat</DetailHeader>
                    <DetailRow>
                      <DetailLabel>Nama</DetailLabel>
                      <DetailValue>{studentData.emergencyContactName}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Nomor Telepon / HP</DetailLabel>
                      <DetailValue>{studentData.emergencyContactPhone}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Hubungan dengan siswa</DetailLabel>
                      <DetailValue>{studentData.emergencyContactRelationship}</DetailValue>
                    </DetailRow>
                  </DetailGroup>
                </DetailContainer>
              </DetailSection>
            </ProfileCard>
          </MainSection>
        </ProfileContent>
      </PageContainer>
    );
  }

  // Home page view
  return (
    <PageContainer>
      {showAnnouncement && !announcementsLoading && announcements.length > 0 && (
        <PopupOverlay>
          <AnnouncementPopupContent>
            <PopupHeader>
              <h3>Pengumuman Sekolah</h3>
              <CloseButton onClick={() => setShowAnnouncement(false)}>×</CloseButton>
            </PopupHeader>
            <AnnouncementScrollContainer>
              {announcementsLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '2rem',
                  color: 'var(--text-light)',
                  fontStyle: 'italic'
                }}>
                  Memuat pengumuman...
                </div>
              ) : announcements.length === 0 ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '2rem',
                  color: 'var(--text-light)',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  Tidak ada pengumuman saat ini
                </div>
              ) : (
                announcements.map((announcement, index) => (
                  <AnnouncementItem key={announcement.id} type={announcement.type}>
                    <AnnouncementHeader>
                      <AnnouncementTitle>{announcement.title}</AnnouncementTitle>
                      <AnnouncementDate>{formatTanggalIndo(announcement.date)}</AnnouncementDate>
                    </AnnouncementHeader>
                    <AnnouncementMessage>{announcement.message}</AnnouncementMessage>
                    {/* <AnnouncementTypeTag type={announcement.type}>
                      {announcement.type === 'important' && '📢 Penting'}
                      {announcement.type === 'info' && 'ℹ️ Informasi'}
                      {announcement.type === 'event' && '📅 Acara'}
                      {announcement.type === 'warning' && '⚠️ Peringatan'}
                    </AnnouncementTypeTag> */}
                  </AnnouncementItem>
                ))
              )}
            </AnnouncementScrollContainer>
          </AnnouncementPopupContent>
        </PopupOverlay>
      )}
      <PageHeader>
        <h1>Status Siswa</h1>
      </PageHeader>

      {/* Jam Kersos Detail Popup */}
      {showKersosDetail && (
        <PopupOverlay>
          <PopupContent style={{ maxWidth: 600 }}>
            <PopupHeader>
              <h3>
                <span role="img" aria-label="Jam Kersos">🤝</span>
                Detail Jam Kersos
              </h3>
              <CloseButton onClick={() => setShowKersosDetail(false)}>×</CloseButton>
            </PopupHeader>
            <PopupBody>
              {/* Ketidaksesuaian Table (Negative Hours) */}
              {kersosDetails.filter(row => parseFloat(row.jam) < 0).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ 
                    color: '#d32f2f', 
                    fontWeight: 600, 
                    fontSize: '1.1rem', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{ marginRight: '8px' }}>⚠️</span>
                    Ketidaksesuaian
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#ffebee' }}>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Pelanggaran</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jam Kersos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kersosDetails.filter(row => parseFloat(row.jam) < 0).map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                              {row.pelanggaran && row.pelanggaran !== 'N/A' && (
                                <>
                                  <span style={{ fontSize: '13px' }}>Kategori: {row.pelanggaran}</span>
                                  <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                </>
                              )}
                              { row.description }
                            </td>

                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{row.jam}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Penyesuaian Table (Positive Hours) */}
              {kersosDetails.filter(row => parseFloat(row.jam) > 0).length > 0 && (
                <div>
                  <h4 style={{ 
                    color: '#2e7d32', 
                    fontWeight: 600, 
                    fontSize: '1.1rem', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{ marginRight: '8px' }}>✅</span>
                    Penyesuaian
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#e8f5e8' }}>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Penyesuaian</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jam Kersos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kersosDetails.filter(row => parseFloat(row.jam) > 0).map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.pelanggaran}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{row.jam}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Show message if no data */}
              {kersosDetails.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: '#666', 
                  fontStyle: 'italic' 
                }}>
                  Tidak ada data jam kersos
                </div>
              )}
            </PopupBody>
          </PopupContent>
        </PopupOverlay>
      )}
      
      <ProfileContent>
        <MainSection>
          <ProfileCard>
            <ProfileHeader>
              <AvatarContainer>
                <Avatar 
                  src={studentData.avatarUrl} 
                  alt={studentData.name}
                  onError={(e) => {
                    e.target.src = '/images/user.png';
                  }}
                />
              </AvatarContainer>
              <ProfileInfo>
                <h2>{studentData.name}</h2>
                <InfoRow>
                  <InfoItem>Kelas: {studentData.class}</InfoItem>
                  <InfoDivider>|</InfoDivider>
                  <InfoItem>NIS: {studentData.nis}</InfoItem>
                  {studentData.wali_kelas_name && (
                    <>
                      <InfoDivider>|</InfoDivider>
                      <InfoItem>Wali Kelas: {studentData.wali_kelas_name}</InfoItem>
                    </>
                  )}
                </InfoRow>
              </ProfileInfo>
            </ProfileHeader>
            
            <StatGrid>
              {studentData.stats.map((stat, index) => (
                <StatCard 
                  key={index}
                  label={stat.label}
                  value={stat.value}
                  icon={stat.icon}
                  bgColor={stat.bgColor}
                  onClick={
                    stat.label === 'Jam Kersos'
                      ? () => setShowKersosDetail(true)
                      : stat.label === 'Jam Lembur'
                        ? () => setShowLemburDetail(true)
                        : stat.label === 'Poin Apresiasi'
                          ? () => setShowApresiasiDetail(true)
                          : stat.label === 'Ketidakhadiran'
                            ? () => setShowKetidakhadiranDetail(true)
                            : stat.label === 'Poin Sikap & SP'
                              ? handleShowSPDetail
                              : undefined
                  }
                  style={{ cursor: stat.label === 'Jam Kersos' || stat.label === 'Jam Lembur' || stat.label === 'Poin Apresiasi' || stat.label === 'Ketidakhadiran' || stat.label === 'Poin Sikap & SP' ? 'pointer' : 'default' }}
                />
              ))}

      {/* Poin Apresiasi Detail Popup */}
      {showApresiasiDetail && (
        <PopupOverlay>
          <PopupContent>
            <PopupHeader>
              <h3>
                <span role="img" aria-label="Poin Apresiasi">🌟</span>
                Detail Poin Apresiasi
              </h3>
              <CloseButton onClick={() => setShowApresiasiDetail(false)}>×</CloseButton>
            </PopupHeader>
            <PopupBody>
              {/* Ketidaksesuaian Table (Negative Points) */}
              {apresiasiDetails.filter(row => parseFloat(row.poin) < 0).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    fontWeight: 600, 
                    color: '#c01c28', 
                    display: 'flex', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ marginRight: '8px', fontSize: '1.2rem' }}>⚠️</span>
                    Ketidaksesuaian
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#ffebee' }}>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Pelanggaran</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin Apresiasi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apresiasiDetails.filter(row => parseFloat(row.poin) < 0).map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.jenis}</td>
                            <td style={{ 
                              padding: '8px', 
                              border: '1px solid #e0e0e0', 
                              textAlign: 'center',
                              color: '#c01c28',
                              fontWeight: 'bold'
                            }}>{row.poin}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Penyesuaian Table (Positive Points) */}
              {apresiasiDetails.filter(row => parseFloat(row.poin) > 0).length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    fontWeight: 600, 
                    color: '#2ec27e', 
                    display: 'flex', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ marginRight: '8px', fontSize: '1.2rem' }}>✅</span>
                    Penyesuaian
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#e8f5e8' }}>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Apresiasi</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin Apresiasi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apresiasiDetails.filter(row => parseFloat(row.poin) > 0).map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.jenis}</td>
                            <td style={{ 
                              padding: '8px', 
                              border: '1px solid #e0e0e0', 
                              textAlign: 'center',
                              color: '#2ec27e',
                              fontWeight: 'bold'
                            }}>{row.poin}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* No Data Message */}
              {apresiasiDetails.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: '#666', 
                  fontStyle: 'italic' 
                }}>
                  Tidak ada data poin apresiasi
                </div>
              )}
            </PopupBody>
          </PopupContent>
        </PopupOverlay>
      )}

      {/* Ketidakhadiran Detail Popup */}
      {showKetidakhadiranDetail && (
        <PopupOverlay>
          <PopupContent>
            <PopupHeader>
              <h3>
                <span role="img" aria-label="Ketidakhadiran">📋</span>
                Detail Ketidakhadiran
              </h3>
              <CloseButton onClick={() => setShowKetidakhadiranDetail(false)}>×</CloseButton>
            </PopupHeader>
            <PopupBody>
              {ketidakhadiranDetails.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                    <thead>
                      <tr style={{ background: '#e8f1fd' }}>
                        <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tgl Lapor</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Kelas</th>
                        <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Hari</th>
                        <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ketidakhadiranDetails.map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                          <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{row.jenis}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{row.kelas}</td>
                          <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{row.hari}</td>

                          <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                            {row.laporan_kts_id !== null && (
                              <>
                                <span style={{ fontSize: '13px' }}>{row.kategori}</span>
                                <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                {row.keterangan}
                              </>
                            )}
                            {row.ijin_tidakmasuk_id !== null && (
                              <>
                                {row.kategori}
                              </>
                            )}
                            </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: '#666', 
                  fontStyle: 'italic' 
                }}>
                  Tidak ada data ketidakhadiran
                </div>
              )}
            </PopupBody>
          </PopupContent>
        </PopupOverlay>
      )}

      {/* Surat Peringatan Detail Popup */}
      {showSPDetail && (
        <PopupOverlay>
          <PopupContent>
            <PopupHeader>
              <h3>
                <span role="img" aria-label="Surat Peringatan">⚠️</span>
                Detail Poin Sikap & SP
              </h3>
              <CloseButton onClick={() => setShowSPDetail(false)}>×</CloseButton>
            </PopupHeader>
            <PopupBody>
              {detailsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Memuat rincian...</div>
              ) : (
                <>
                  <div style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#333', fontWeight: 600, fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>🚫</span>
                      Rincian Poin Sikap
                    </h4>
                    {poinSikapDetails.filter(row => row.point < 0).length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{ color: '#d32f2f', fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem' }}>Ketidaksesuaian</h5>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead><tr style={{ background: '#ffebee' }}>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin</th>
                          </tr></thead>
                          <tbody>{poinSikapDetails.filter(row => row.point < 0).map((row, idx) => (
                            <tr key={idx}><td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.created_at)}</td>
                           <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                              {row.pelanggaran && row.pelanggaran !== 'N/A' && (
                                <>
                                  <span style={{ fontSize: '13px' }}>Kategori: {row.pelanggaran}</span>
                                  <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                </>
                              )}
                              {row.description}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{row.point}</td></tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                    {poinSikapDetails.filter(row => row.point > 0).length > 0 && (
                      <div>
                        <h5 style={{ color: '#2e7d32', fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem' }}>Penyesuaian</h5>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead><tr style={{ background: '#e8f5e8' }}>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin</th>
                          </tr></thead>
                          <tbody>{poinSikapDetails.filter(row => row.point > 0).map((row, idx) => (
                            <tr key={idx}><td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.created_at)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.description}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{`+${row.point}`}</td></tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                    {poinSikapDetails.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '1rem', color: '#666', fontStyle: 'italic' }}>Tidak ada data poin sikap.</div>
                    )}
                  </div>

                  <h4 style={{ color: '#333', fontWeight: 600, fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }}>📄</span>
                    Riwayat Surat Peringatan
                  </h4>
                  {spDetails.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                        <thead>
                          <tr style={{ background: '#e8f1fd' }}>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal SP</th>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis SP</th>
                            <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Diterbitkan oleh</th>
                          </tr>
                        </thead>
                        <tbody>
                          {spDetails.map((row, idx) => (
                            <tr key={idx}>
                              <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                              <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{row.jenis}</td>
                              <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.diterbitkan}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666', fontStyle: 'italic' }}>
                      Tidak ada data surat peringatan
                    </div>
                  )}
                </>
              )}
            </PopupBody>
          </PopupContent>
        </PopupOverlay>
      )}

      {/* Jam Lembur Detail Popup */}
      {showLemburDetail && (
        <PopupOverlay>
          <PopupContent>
            <PopupHeader>
              <h3>
                <span role="img" aria-label="Jam Lembur">⏰</span>
                Detail Jam Lembur
              </h3>
              <CloseButton onClick={() => setShowLemburDetail(false)}>×</CloseButton>
            </PopupHeader>
            <PopupBody>
              {/* Ketidaksesuaian Table (Negative Hours) */}
              {lemburDetails.filter(row => parseFloat(row.jam) < 0).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                    <h4 style={{ 
                      color: '#d32f2f', 
                      fontWeight: 600, 
                      fontSize: '1.1rem', 
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{ marginRight: '8px' }}>⚠️</span>
                      Ketidaksesuaian
                    </h4>
                    
                    {/* Tooltip Icon */}
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <span 
                        style={{ 
                          cursor: 'help',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#2196F3',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          userSelect: 'none'
                        }}
                        title={`Sisa Kompen Murni: ${kompenStats.sisa_kompen_murni} jam\nJam lembur karena ketidakhadiran di bengkel. Jam lembur harus bernilai 0 sebagai salah satu syarat kenaikan dan kelulusan.\n\nSisa Kompen Alat: ${kompenStats.sisa_kompen_alat} jam\nJam lembur karena alat/benda. Jam lembur bisa ditutup dengan jam surplus atau dialihkan ke tahun depan. Ijazah tidak akan diberikan jika jam lembur belum 0.`}
                      >
                        ?
                      </span>
                    </div>
                  </div>
                  
                  {/* Info boxes for Kompen Stats */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '12px', 
                    marginBottom: '1rem',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#fff3e0', 
                      borderRadius: '8px',
                      border: '1px solid #ffb74d'
                    }}>
                      <div style={{ fontWeight: 600, color: '#e65100', marginBottom: '4px' }}>
                        Sisa Kompen Murni
                      </div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#d32f2f' }}>
                        {kompenStats.sisa_kompen_murni} jam
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                        Jam lembur karena ketidakhadiran di bengkel. Harus 0 untuk syarat kenaikan dan kelulusan.
                      </div>
                    </div>
                    
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '8px',
                      border: '1px solid #64b5f6'
                    }}>
                      <div style={{ fontWeight: 600, color: '#0d47a1', marginBottom: '4px' }}>
                        Sisa Kompen Alat
                      </div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#d32f2f' }}>
                        {kompenStats.sisa_kompen_alat} jam
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                        Jam lembur karena alat/benda. Bisa ditutup dengan jam surplus atau dialihkan ke tahun depan. Ijazah tidak akan diberikan jika jam lembur belum 0.
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#ffebee' }}>
                          
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jumlah Jam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lemburDetails.filter(row => parseFloat(row.jam) < 0).map((row, idx) => (
                          <tr key={idx}>
                            
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                              {row.kategori && row.kategori !== 'N/A' && (
                                <>
                                  <span style={{ fontSize: '13px' }}>Kategori: {row.kategori}</span>
                                  <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                </>
                              )}
                              {row.keterangan}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{row.jam}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Penyesuaian Table (Positive Hours) */}
              {lemburDetails.filter(row => parseFloat(row.jam) > 0).length > 0 && (
                <div>
                  <h4 style={{ 
                    color: '#2e7d32', 
                    fontWeight: 600, 
                    fontSize: '1.1rem', 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{ marginRight: '8px' }}>✅</span>
                    Penyesuaian
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#e8f5e8' }}>
                          
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jumlah Jam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lemburDetails.filter(row => parseFloat(row.jam) > 0).map((row, idx) => (
                          <tr key={idx}>
                            
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.keterangan}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{row.jam}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Show message if no data */}
              {lemburDetails.filter(row => parseFloat(row.jam) !== 0).length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: '#666', 
                  fontStyle: 'italic' 
                }}>
                  Tidak ada data jam lembur
                </div>
              )}
            </PopupBody>
          </PopupContent>
        </PopupOverlay>
      )}


            </StatGrid>
          </ProfileCard>
        </MainSection>
        
        <SideSection>
          <ActivityCard>
            <h3>Request Terbaru</h3>
            {requestsLoading ? (
              <LoadingContainer>
                <LoadingText>Memuat request terbaru...</LoadingText>
              </LoadingContainer>
            ) : studentData.activities.length === 0 ? (
              <EmptyState>
                <EmptyStateText>Belum ada request yang dibuat</EmptyStateText>
              </EmptyState>
            ) : (
              <ActivityList>
                {studentData.activities.map((activity, index) => (
                  <ActivityItem key={index} type={activity.type}>
                    <ActivityContent>
                      <ActivityDate>{formatDate(activity.date)}</ActivityDate>
                      <ActivityTitle>{activity.title}</ActivityTitle>
                    </ActivityContent>
                    <ViewButton onClick={() => handleViewActivity(activity)}>
                      View
                    </ViewButton>
                  </ActivityItem>
                ))}
              </ActivityList>
            )}
          </ActivityCard>
        </SideSection>
      </ProfileContent>
      <WhatsAppButton
        aria-label="WhatsApp Pamong"
        title="Hubungi WA Pamong"
        onClick={() => window.open('https://api.whatsapp.com/send/?phone=6281548011463&text&type=phone_number&app_absent=0', '_blank')}
      >
        <WhatsAppIcon size={window.innerWidth <= 576 ? 28 : 38} />
      </WhatsAppButton>
      <WhatsAppLabel>WA Pamong</WhatsAppLabel>
      <ChatbotButton
        aria-label="Chatbot"
        title="Buka Chatbot"
        onClick={() => setShowChatbot(true)}
      >
        <ChatIcon size={window.innerWidth <= 576 ? 28 : 38} />
      </ChatbotButton>
      {showChatbot && (
        <ChatbotModalOverlay onClick={() => setShowChatbot(false)}>
          <ChatbotModalContent onClick={e => e.stopPropagation()}>
            <ChatbotModalHeader>
              <ChatbotModalTitle>Chatbot Bantuan</ChatbotModalTitle>
              <ChatbotModalClose onClick={() => setShowChatbot(false)}>&times;</ChatbotModalClose>
            </ChatbotModalHeader>
            <ChatbotIframe
              src="https://automation.canvaswebdesign.com/webhook/6ea2ebe9-ae12-4df0-ab4b-ee13e9cecdba/chat"
              title="Chatbot"
              allow="microphone; camera"
            />
          </ChatbotModalContent>
        </ChatbotModalOverlay>
      )}
    </PageContainer>
  );
};

// Helper function to format dates
const formatDate = (dateString) => {
  const options = { month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Format tanggal ke "11 Mei 2025" (Indonesian)
function formatTanggalIndo(dateString) {
  const bulan = [
    'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
    'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
  ];
  const d = new Date(dateString);
  const tgl = d.getDate();
  const bln = bulan[d.getMonth()];
  const thn = d.getFullYear();
  return `${tgl} ${bln} ${thn}`;
}


const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const ProfileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProfileCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
`;

const ProfileHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 576px) {
    padding: 1rem;
    flex-direction: row;
    text-align: left;
    gap: 1rem;
  }
`;

const AvatarContainer = styled.div`
  flex-shrink: 0;
  
  @media (max-width: 576px) {
    margin-bottom: 0;
  }
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
  }
`;

const ProfileInfo = styled.div`
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 576px) {
    width: auto;
    text-align: left;
    
    h2 {
      font-size: 1.25rem;
      margin-bottom: 0.25rem;
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 5px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    margin-top: 2px;
  }
`;

const InfoItem = styled.div`
  color: var(--text-dark);
  font-size: 1.125rem;
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.875rem;
    margin-bottom: 0;
  }
`;

const InfoDivider = styled.span`
  color: #ddd;
  font-weight: 300;
  
  @media (max-width: 576px) {
    display: none;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    padding: 1rem;
  }
  
  @media (max-width: 576px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0.75rem;
    & > * {
      min-height: 80px;
    }
  }
`;

const ActivityCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--border);
    color: var(--text-dark);
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActivityItem = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background-color: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      switch(props.type) {
        case 'surat-ijin': return '#2c3b56'; // dark blue for dark mode
        case 'laporan': return '#1e3a32';    // dark green for dark mode
        default: return '#333333';
      }
    } else {
      switch(props.type) {
        case 'surat-ijin': return '#e8f1fd'; // light blue for light mode
        case 'laporan': return '#e6f8f5';    // light green for light mode
        default: return '#f5f5f5';
      }
    }
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--box-shadow);
  }
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityDate = styled.div`
  font-size: 0.75rem;
  color: var(--text-lighter);
  margin-bottom: 0.25rem;
`;

const ActivityTitle = styled.div`
  font-weight: 500;
  color: var(--text-dark);
`;

const ViewButton = styled.button`
  background: var(--primary, #3354a5);
  color: white;
  border: none;
  border-radius: 6px;
  padding: ${props => props.small ? '0.25rem 0.6rem' : '0.4rem 0.8rem'};
  font-size: ${props => props.small ? '0.7rem' : '0.8rem'};
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;
  
  &:hover {
    background: #2a4580;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const UpcomingCard = styled(ActivityCard)``;

const DeadlineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DeadlineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const DeadlineInfo = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const DeadlineDate = styled.div`
  font-weight: 600;
  color: var(--primary);
`;

const DeadlineTitle = styled.div`
  color: var(--text-dark);
`;

// Styled components for detailed profile view
const DetailSection = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--border);
`;

const DetailContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme === 'dark' ? '#333333' : '#f2f2f2'};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: background-color 0.3s;
`;

const DetailHeader = styled.h3`
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border);
  font-size: 1.25rem;
  color: var(--primary);
  font-weight: 600;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px dashed var(--border);
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const DetailLabel = styled.div`
  flex: 0 0 40%;
  font-weight: 600;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  flex: 0 0 60%;
  color: var(--text-dark);
  font-weight: 500;
`;

const DetailValueLong = styled(DetailValue)`
  @media (max-width: 576px) {
    margin-top: 0.25rem;
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopupContent = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;
  border: 1px solid var(--border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const PopupHeader = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #e8f1fd; // soft blue like the stats cards

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    

  }
`;

const PopupBody = styled.div`
  padding: 1.75rem;
  font-size: 1.25rem;
  line-height: 1.6;
  color: var(--text-dark);
  background: linear-gradient(to bottom right, var(--background), #f8faff);
  overflow-y: auto;
  flex: 1;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--background-light);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 3px;
    opacity: 0.7;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    opacity: 1;
  }
  
  @media (max-width: 576px) {
    padding: 1.25rem;
    font-size: 1.1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  color: var(--primary);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  margin-right: -0.5rem;

  &:hover {
    background-color: rgba(51, 84, 165, 0.1);
    color: var(--primary);
  }

  &:active {
    background-color: rgba(51, 84, 165, 0.2);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const LoadingText = styled.div`
  color: var(--text-light);
  font-style: italic;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const EmptyStateText = styled.div`
  color: var(--text-light);
  font-style: italic;
  text-align: center;
`;

const AnnouncementPopupContent = styled(PopupContent)`
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const AnnouncementScrollContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 1rem;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--background-light);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 3px;
    opacity: 0.7;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    opacity: 1;
  }
`;

const AnnouncementItem = styled.div`
  background: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    switch(props.type) {
      case 'important': return isDark ? '#4a2c2a' : '#fff5f5';
      case 'warning': return isDark ? '#4a3d2a' : '#fffbf0';
      case 'event': return isDark ? '#2a3d4a' : '#f0f8ff';
      case 'info': return isDark ? '#2a4a3d' : '#f0fff4';
      default: return isDark ? '#333333' : '#f8f9fa';
    }
  }};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  border-left: 4px solid ${props => {
    switch(props.type) {
      case 'important': return '#e74c3c';
      case 'warning': return '#f39c12';
      case 'event': return '#3498db';
      case 'info': return '#27ae60';
      default: return '#95a5a6';
    }
  }};
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AnnouncementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const AnnouncementTitle = styled.h4`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-dark);
  line-height: 1.3;
`;

const AnnouncementDate = styled.span`
  font-size: 0.875rem;
  color: var(--text-light);
  font-weight: 500;
  white-space: nowrap;
`;

const AnnouncementMessage = styled.p`
  margin: 0 0 1rem 0;
  color: var(--text-dark);
  line-height: 1.5;
  font-size: 0.95rem;
`;

const AnnouncementTypeTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch(props.type) {
      case 'important': return '#e74c3c';
      case 'warning': return '#f39c12';
      case 'event': return '#3498db';
      case 'info': return '#27ae60';
      default: return '#95a5a6';
    }
  }};
  color: white;
`;

// Standardized table components for consistent design across all stat detail popups
const StandardTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const StandardTableHeader = styled.thead`
  tr {
    background: ${props => props.bgColor || '#e8f1fd'};
  }
  
  th {
    padding: 12px 8px;
    border: 1px solid #e0e0e0;
    font-weight: 600;
    color: var(--text-dark);
    text-align: center;
    font-size: 0.95rem;
  }
`;

const StandardTableBody = styled.tbody`
  tr:nth-child(even) {
    background-color: var(--background-light);
  }
  
  td {
    padding: 10px 8px;
    border: 1px solid #e0e0e0;
    color: var(--text-dark);
    vertical-align: middle;
  }
`;

const TableSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 1rem;
  }
`;

const TableSectionTitle = styled.h4`
  margin: 0 0 1rem 0;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  color: ${props => props.color || 'var(--text-dark)'};
  
  span {
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const EmptyDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
  background-color: var(--background-light);
  border-radius: 8px;
  margin: 1rem 0;
`;

export default ProfilePage;